import { useCallback, useMemo, useRef } from 'react'
import { useDispatch } from 'react-redux'

import { FormHandles } from '@unform/core'

import Panel from '~/components/Panel'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'
import EditButton from '~/modules/retailMedia/components/EditButton'
import { useEditPanel } from '~/modules/retailMedia/hooks/useEditPanel'
import { patch } from '~/modules/retailMedia/store/industry/campaign/actions'

import { toAdditionalAssetsPayload, toFormData } from './dtos'
import EditMode from './EditMode'
import ViewMode from './ViewMode'

const AdditionalAssetsBannerVideo: React.FC<
  AdditionalAssetsBannerVideoProps
> = ({ data, className }) => {
  const { t } = useAPPTranslation()
  const dispatch = useDispatch()

  const {
    isEditMode,
    setIsEditMode,
    loadingSave,
    canEdit,
    handleSuccessSubmit
  } = useEditPanel({ canEditCampaign: data?.canEditCampaign })

  const additionalAssets = useMemo(
    () => data?.settings?.additional_assets || [],
    [data?.settings?.additional_assets]
  )

  const mainBackground = useMemo(
    () =>
      additionalAssets.find(
        (asset: AdditionalAsset) => asset.name === 'main_background'
      ),
    [additionalAssets]
  )

  const secondaryBackground = useMemo(
    () =>
      additionalAssets.find(
        (asset: AdditionalAsset) => asset.name === 'secondary_background'
      ),
    [additionalAssets]
  )

  const formRef = useRef<FormHandles>(null)

  const handleSubmit = useCallback(
    (formData: AdditionalAssetsBannerVideoFormData) => {
      dispatch(
        patch({
          body: {
            settings: { additional_assets: toAdditionalAssetsPayload(formData) }
          },
          id: data.id,
          successFeedbackMsg: t('rm:actions.campaign.edit.successEdit'),
          onSuccess: handleSuccessSubmit,
          values: { patchKey: 'settings' }
        })
      )
    },
    [data.id, dispatch, t, handleSuccessSubmit]
  )

  const initialDataForForm = useMemo(
    () => toFormData(additionalAssets),
    [additionalAssets]
  )

  return (
    <Panel
      title={t('rm:campaign.additionalAssets.title')}
      description={t('rm:campaign.additionalAssets.description')}
      className={className}
      template={isEditMode ? 'borderWarning' : 'default'}
      headerSideContent={
        canEdit && (
          <EditButton
            onClick={() => setIsEditMode(e => !e)}
            isActive={isEditMode}
          />
        )
      }
    >
      {!isEditMode && (
        <ViewMode
          mainBackground={mainBackground}
          secondaryBackground={secondaryBackground}
        />
      )}

      {isEditMode && (
        <EditMode
          formRef={formRef}
          initialData={initialDataForForm}
          mainBackground={mainBackground}
          secondaryBackground={secondaryBackground}
          loadingSave={loadingSave}
          onSubmit={handleSubmit}
          onCancel={() => setIsEditMode(false)}
        />
      )}
    </Panel>
  )
}

export default AdditionalAssetsBannerVideo
